import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';

import type {
  IResourceClientMember,
  IResourceDatabaseField,
  IResourceDatabaseFieldType,
  IResourceDatabaseRecord,
  IResourceDatabaseRecordWithMeta,
  IResourceViewProps,
} from '@/api/types';
import type { ITreeFolder } from '@/components/common';
import type { IFileProxy } from '@/components/common/form/types';
import type { IDateFormatter } from '@/types/date';

import { Button, ClientMemberSelect, DatabaseFieldInput } from '@/components/common';
import { createDatabaseItemResolver } from './modalSchemas';
import { DATE_ISO_FORMAT, TIME_ISO_FORMAT } from '@/config/variables';
import { useTimezone } from '@/hooks';
import { ResourceDatabaseFieldTypeId } from '@/api/enums';
import { createFileProxyFromPath, formData } from '@/helpers';
import { ResourceMetadataSection } from './ResourceMetadataSection';
import { UserType } from '@/store/enums';

const formatDateType = (type: ResourceDatabaseFieldTypeId, date: string | null, formatDate: IDateFormatter) => {
  if (!date) return '';

  switch (type) {
    case ResourceDatabaseFieldTypeId.Date:
      return formatDate(date, DATE_ISO_FORMAT);
    case ResourceDatabaseFieldTypeId.Time:
      return formatDate(date, TIME_ISO_FORMAT);
    default:
      return '';
  }
};

const defaultValues = (
  fields: IResourceDatabaseField[],
  formatDate: IDateFormatter,
): Record<string, string | number | IFileProxy[] | null> =>
  fields.reduce<Record<string, string | number | IFileProxy[] | null>>((record, field) => {
    const fieldValue = field.defaultValue;
    const columnId = field.columnName;

    switch (field.rtxtype_Id) {
      case ResourceDatabaseFieldTypeId.FileAttachment: {
        return {
          ...record,
          [columnId]: [],
        };
      }
      case ResourceDatabaseFieldTypeId.TextMultiline:
      case ResourceDatabaseFieldTypeId.Currency: {
        return {
          ...record,
          [columnId]: fieldValue || '',
        };
      }
      case ResourceDatabaseFieldTypeId.DateTime: {
        return {
          ...record,
          [`${columnId}-date`]: fieldValue
            ? formatDateType(ResourceDatabaseFieldTypeId.Date, fieldValue, formatDate)
            : '',
          [`${columnId}-time`]: fieldValue
            ? formatDateType(ResourceDatabaseFieldTypeId.Time, fieldValue, formatDate)
            : '',
        };
      }
      default: {
        return { ...record, [columnId]: fieldValue };
      }
    }
  }, {});

const mapResourceToFormValues = (
  resource: IResourceDatabaseRecord,
  fields: IResourceDatabaseField[],
  formatDate: IDateFormatter,
): Record<string, string | number | IFileProxy[] | null> =>
  fields.reduce<Record<string, string | number | IFileProxy[] | null>>((record, field) => {
    const fieldValue = resource[field.name];
    const columnId = field.columnName;

    switch (field.rtxtype_Id) {
      case ResourceDatabaseFieldTypeId.FileAttachment: {
        const file = createFileProxyFromPath(fieldValue as string | null);
        return {
          ...record,
          [columnId]: file,
        };
      }
      case ResourceDatabaseFieldTypeId.Checkbox: {
        return {
          ...record,
          [columnId]: Number(fieldValue),
        };
      }
      case ResourceDatabaseFieldTypeId.TextMultiline:
      case ResourceDatabaseFieldTypeId.Currency:
      case ResourceDatabaseFieldTypeId.Date: {
        return {
          ...record,
          [columnId]: fieldValue || '',
        };
      }
      case ResourceDatabaseFieldTypeId.DateTime: {
        return {
          ...record,
          [`${columnId}-date`]: fieldValue
            ? formatDateType(ResourceDatabaseFieldTypeId.Date, fieldValue as string, formatDate)
            : '',
          [`${columnId}-time`]: fieldValue
            ? formatDateType(ResourceDatabaseFieldTypeId.Time, fieldValue as string, formatDate)
            : '',
        };
      }
      case ResourceDatabaseFieldTypeId.Time: {
        const value = fieldValue as string;
        return {
          ...record,
          [columnId]: value ? value.substring(0, value.lastIndexOf('.')) : '',
        };
      }
      case ResourceDatabaseFieldTypeId.MemberList: {
        const value = fieldValue as string;
        return {
          ...record,
          [columnId]: value ? Number.parseInt(value.split('|')[0], 10) : '',
        };
      }
      default: {
        return {
          ...record,
          [columnId]: fieldValue,
        };
      }
    }
  }, {});

interface IDatabaseItemFormProps {
  onSubmit: (data: Record<string, string | number | null>) => void;
  onCancel: () => void;
  fieldProps: IResourceViewProps[];
  existingResource?: IResourceDatabaseRecord;
  foldersList: ITreeFolder[];
  fieldTypeDict: IResourceDatabaseFieldType[];
  memberList: IResourceClientMember[];
  userType: UserType;
  createdByColumnName?: string;
}

const DatabaseItemForm = ({
  onSubmit,
  onCancel,
  existingResource,
  fieldProps,
  fieldTypeDict,
  memberList,
  userType,
  createdByColumnName,
}: IDatabaseItemFormProps): React.ReactElement => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const fields = useMemo(() => fieldProps.map(prop => prop.field), [fieldProps]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, touchedFields },
    control,
  } = useForm<Record<string, string | number | IFileProxy[] | null>>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(createDatabaseItemResolver(fields, fieldTypeDict)),
    defaultValues: existingResource
      ? mapResourceToFormValues(existingResource, fields, formatDateTime)
      : defaultValues(fields, formatDateTime),
  });

  const metaData = useMemo(() => {
    if (userType > UserType.agent && existingResource) {
      const resourceWithMeta = existingResource as IResourceDatabaseRecordWithMeta;

      return {
        dateCreated: resourceWithMeta['Date Created'],
        createdByName: resourceWithMeta['Created by']?.split('|')[1],
        createdBy: Number.parseInt(resourceWithMeta['Created by']?.split('|')[0], 10),
        dateModified: resourceWithMeta['Date Modified'],
        modifiedByName: resourceWithMeta['Modified by']?.split('|')?.[1] || null,
        modifiedBy: Number.parseInt(resourceWithMeta['Modified by']?.split('|')?.[0], 10) || null,
      };
    }

    return undefined;
  }, [existingResource, userType]);

  useEffect(() => {
    if (existingResource) {
      const newFormValues = mapResourceToFormValues(existingResource, fields, formatDateTime);

      if (metaData && createdByColumnName) {
        newFormValues[createdByColumnName] = metaData.createdBy;
      }

      reset(newFormValues);
    }
  }, [existingResource, reset, formatDateTime, fields, metaData, createdByColumnName]);

  const formDataOptions = {
    register,
    touchedFields,
    errors,
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {fieldProps
        .filter(prop => !prop.field.isInternal)
        .sort((propA, propB) => propA.position - propB.position)
        .map(prop => (
          <DatabaseFieldInput
            key={prop.field.id}
            field={prop.field}
            formDataOptions={formDataOptions}
            control={control}
            memberList={memberList}
          />
        ))}
      {userType > UserType.agent && (
        <>
          {existingResource && createdByColumnName && (
            <ClientMemberSelect
              name={createdByColumnName}
              label={t`resources.modals.createdBy`}
              clientMemberList={memberList}
              metaData={metaData}
              {...formData(createdByColumnName, formDataOptions)}
            />
          )}
          <ResourceMetadataSection item={metaData} />
        </>
      )}
      <FormControl>
        <StyledButton color="blueOutline" className="me-3" onClick={onCancel}>
          {t`common.btnCancel`}
        </StyledButton>
        <StyledButton color="blue" type="submit">
          {existingResource ? t`common.btnSave` : t`common.btnCreate`}
        </StyledButton>
      </FormControl>
    </form>
  );
};

const StyledButton = styled(Button)`
  width: 190px;
`;

const FormControl = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

export { DatabaseItemForm };
